import { UnitTypeUiModel } from '../../../shared/ui-models/unit-type.ui-model';
import { BulkUpdate } from '../../../shared/models/unit-types.model';
import { ReservationMode } from '../../../shared/enums/reservation-mode.enum';
import {
  adjustCustomPrices,
  AdjustmentPeriod,
  CustomPriceAdjustmentResult,
  PriceAdjustmentChangeType,
  PriceAdjustmentDirection
} from '../../../UnitTypeModule/shared/services/custom-price-adjustment.service';

/** Why a unit in the project is left untouched by a percentage adjustment. */
export type IneligibleReason = 'not-daily' | 'no-base-price' | 'no-change';

export const INELIGIBLE_LABELS: Record<IneligibleReason, string> = {
  'not-daily': 'Monthly unit — no dynamic prices',
  'no-base-price': 'No base price in USD',
  'no-change': 'Nothing to change in these periods'
};

export interface UnitPricePlan {
  unit: UnitTypeUiModel;
  adjusted: number;
  created: number;
  customPrices: CustomPriceAdjustmentResult['customPrices'];
  ineligible?: IneligibleReason;
}

export interface ProjectPriceAdjustmentInput {
  unitTypes: UnitTypeUiModel[];
  periods: AdjustmentPeriod[];
  percentage: number;
  direction: PriceAdjustmentDirection;
}

/**
 * Works out what a percentage adjustment would do to every unit in a project.
 *
 * Each unit is planned against its own customPrices and its own base price — this is
 * not one price list copied across the project. Units that cannot take a dynamic
 * price are reported as ineligible rather than silently edited:
 *  - monthly units have no dynamic prices at all (the unit screen hides the panel),
 *  - a unit with neither existing dynamic prices nor a base price in USD has nothing
 *    to derive a new price from,
 *  - a unit whose prices the selected periods do not touch has nothing to change.
 */
export function planProjectPriceAdjustment({
  unitTypes,
  periods,
  percentage,
  direction
}: ProjectPriceAdjustmentInput): UnitPricePlan[] {
  return (unitTypes || []).map((unit) => {
    const empty: UnitPricePlan = { unit, adjusted: 0, created: 0, customPrices: [] };
    const existingPrices = unit.customPrices || [];

    if (unit.reservationMode !== ReservationMode.DAILY) {
      return { ...empty, ineligible: 'not-daily' };
    }
    if (!(Number(unit.unitPriceInUSD) > 0) && existingPrices.length === 0) {
      return { ...empty, ineligible: 'no-base-price' };
    }

    const outcome = adjustCustomPrices({
      customPrices: existingPrices,
      periods,
      percentage,
      direction,
      basePriceInUSD: Number(unit.unitPriceInUSD) || 0
    });

    const plan: UnitPricePlan = {
      unit,
      adjusted: outcome.summary.adjusted,
      created: outcome.summary.created,
      customPrices: outcome.customPrices
    };

    const changed = outcome.rows.some(
      (row) => row.changeType !== PriceAdjustmentChangeType.unchanged
    );
    return changed ? plan : { ...plan, ineligible: 'no-change' };
  });
}

/** Splits a list into fixed-size batches, preserving order. */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [items];
  const batches: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size));
  }
  return batches;
}

const EMPTY_MERGE: BulkUpdate = {
  id: '',
  updatedItemsCount: 0,
  successRecords: [],
  errorRecords: [],
  birdnest: { success: 0, failed: 0 },
  rentalsUnited: { success: 0, failed: 0, skipped: 0 },
  records: [],
  updateById: '',
  updateByUser: '',
  updatedAt: 0,
  createdAt: 0
};

/**
 * Merges the per-batch responses of a chunked bulk update into one set of tallies,
 * so a project of any size reports a single honest result.
 */
export function mergeBulkUpdateResults(results: BulkUpdate[]): BulkUpdate {
  return (results || []).reduce<BulkUpdate>(
    (merged, batch) => ({
      ...merged,
      id: merged.id || (batch.id as string) || '',
      updatedItemsCount: merged.updatedItemsCount + (batch.updatedItemsCount || 0),
      successRecords: [...merged.successRecords, ...(batch.successRecords || [])],
      errorRecords: [...merged.errorRecords, ...(batch.errorRecords || [])],
      records: [...(merged.records || []), ...(batch.records || [])],
      birdnest: {
        success: (merged.birdnest?.success || 0) + (batch.birdnest?.success || 0),
        failed: (merged.birdnest?.failed || 0) + (batch.birdnest?.failed || 0)
      },
      rentalsUnited: {
        success: (merged.rentalsUnited?.success || 0) + (batch.rentalsUnited?.success || 0),
        failed: (merged.rentalsUnited?.failed || 0) + (batch.rentalsUnited?.failed || 0),
        skipped: (merged.rentalsUnited?.skipped || 0) + (batch.rentalsUnited?.skipped || 0)
      }
    }),
    { ...EMPTY_MERGE }
  );
}
